import React from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  ReferenceLine,
} from 'recharts';
import { ChartData } from '../types';
import { useIsMobile } from '../../../../hooks/useMobile';

interface StatisticsChartProps {
  chartData: ChartData[];
  color: string;
  suffix: string;
  type: string;
  grouping?: string;
}

export const StatisticsChart: React.FC<StatisticsChartProps> = ({
  chartData,
  grouping = 'day',
}) => {
  const isMobile = useIsMobile();
  const formatMonth = (month: string) => {
    const monthNumber =
      {
        Jan: 0,
        Feb: 1,
        Mar: 2,
        Apr: 3,
        May: 4,
        Jun: 5,
        Jul: 6,
        Aug: 7,
        Sep: 8,
        Oct: 9,
        Nov: 10,
        Dec: 11,
      }[month] ?? 0;

    const currentYear = new Date().getFullYear();
    return new Intl.DateTimeFormat('ru-RU', { month: 'short' }).format(
      new Date(currentYear, monthNumber, 1)
    );
  };

  // Функция для форматирования label в тултипе
  const formatTooltipLabel = (label: any, payload?: any[]) => {
    if (payload && payload.length && payload[0]?.payload?.tooltipLabel) {
      return payload[0].payload.tooltipLabel;
    }

    // Проверка на undefined/null и конвертация в строку
    if (!label) return '';
    const labelStr = String(label);

    // Если это неделя (формат W01, W02, etc)
    if (grouping === 'week' && labelStr.startsWith('W')) {
      return `Неделя ${labelStr.substring(1)}`;
    }

    // Если это месяц (Jan, Feb, Mar, etc)
    if (grouping === 'month') {
      return formatMonth(labelStr);
    }

    // Если это день - форматируем полную дату (YYYY-MM-DD)
    if (labelStr.includes('-')) {
      const date = new Date(labelStr);
      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    }

    // Fallback
    return labelStr;
  };

  // Check if chartData is empty (and demo data was removed)
  if (!chartData || chartData.length === 0) {
    return (
      <div className="w-full h-[25rem] flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500">Нет данных для отображения</p>
        </div>
      </div>
    );
  }

  // Calculate max values and find the maximum to use as a unified scale
  const maxSubscribers = Math.max(...chartData.map((d) => d.subscribers));
  const maxIncome = Math.max(...chartData.map((d) => d.income));

  // Use the same scale for both - find the maximum value overall and round it up
  const overallMax = Math.max(maxSubscribers, maxIncome);
  const roundedMax = Math.ceil(overallMax / 10) * 10;

  // Generate 7 equal ticks for the unified scale
  const unifiedTicks = Array.from({ length: 8 }, (_, i) => Math.round((roundedMax / 7) * i));

  return (
    <div className="w-full relative">
      {/* Vertical dashed lines positioned absolutely */}
      <div className="absolute left-[2.75rem] top-2 bottom-2 w-px border-l-2 border-dashed border-auth-strength-weak z-10"></div>
      <div className="absolute right-[2.75rem] top-2 bottom-2 w-px border-l-2 border-dashed border-primary z-10"></div>

      <ResponsiveContainer
        className="min-h-[17.5rem] md:min-h-[25rem] [&_.recharts-surface]:outline-hidden"
        height={isMobile ? 280 : 400}
        width="100%"
      >
        <AreaChart
          data={chartData}
          margin={{
            top: 10,
            right: 24,
            left: 24,
            bottom: 10,
          }}
        >
          <defs>
            {/* Red gradient for subscribers */}
            <linearGradient id="subscribersGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#F31260" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#F31260" stopOpacity={0} />
            </linearGradient>
            {/* Purple gradient for income */}
            <linearGradient id="incomeGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#9675CE" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#9675CE" stopOpacity={0} />
            </linearGradient>
          </defs>

          {/* Grid with dashed lines - 7 равномерных линий от 0 до макс значения */}
          <CartesianGrid
            stroke="#E4E4E7"
            strokeDasharray="3 3"
            vertical={false}
            horizontalCoordinatesGenerator={(props) => {
              const { height, offset } = props;
              const chartHeight = height - (offset?.top || 0) - (offset?.bottom || 0);
              const step = chartHeight / 7;
              return Array.from({ length: 7 }, (_, i) => (offset?.top || 0) + i * step);
            }}
          />

          {/* Left Y Axis for subscribers - shows subscriber values */}
          <YAxis
            yAxisId="unified"
            orientation="left"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 10, fill: '#F31260', textAnchor: 'end' }}
            domain={[0, roundedMax]}
            ticks={unifiedTicks}
            width={24}
            tickFormatter={(value) => `${value}`}
          />

          {/* Right Y Axis for income - shows income values on the same scale */}
          <YAxis
            yAxisId="unified-right"
            orientation="right"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 10, fill: '#9675CE', textAnchor: 'start' }}
            domain={[0, roundedMax]}
            ticks={unifiedTicks}
            width={24}
            tickFormatter={(value) => `$${value}`}
          />

          {/* Hidden X Axis - needed for proper data mapping */}
          <XAxis dataKey="month" hide={true} />

          <Tooltip
            content={({ label, payload }) => (
              <div className="rounded-lg bg-white shadow-lg border border-default-200 p-3">
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-medium text-default-700">
                    {formatTooltipLabel(label, payload)}
                  </span>
                  {payload?.map((entry, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: entry.color }}
                      />
                      <span className="text-sm text-default-600">
                        {entry.dataKey === 'income' ? 'Доход' : 'Подписчики'}:
                        {entry.dataKey === 'income' ? '$' : ''}
                        {entry.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            cursor={{ stroke: '#E4E4E7', strokeWidth: 1 }}
          />

          {/* Income area (purple) - draws first (behind) */}
          <Area
            yAxisId="unified"
            type="monotone"
            dataKey="income"
            stroke="#9675CE"
            strokeWidth={3}
            fill="url(#incomeGradient)"
            activeDot={{
              r: 4,
              stroke: '#9675CE',
              strokeWidth: 2,
              fill: '#FFFFFF',
            }}
          />

          {/* Subscribers area (red) - draws second (on top) */}
          <Area
            yAxisId="unified"
            type="monotone"
            dataKey="subscribers"
            stroke="#F31260"
            strokeWidth={3}
            fill="url(#subscribersGradient)"
            activeDot={{
              r: 4,
              stroke: '#F31260',
              strokeWidth: 2,
              fill: '#FFFFFF',
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};
